import { useEffect, useState } from "react";
import { useStore } from "@/domain/store";
import { api } from "@/domain/api";
import { cacheReference, getCachedReference } from "@/domain/offlineDb";
import { formatRelativeShort } from "@/design/format";
import type { WorkspaceSeed } from "@/domain/types";

const SEED_CACHE_KEY = "workspace-seed";

/**
 * Loads the workspace seeds from the backend (GET /api/workspace-seed) and
 * hydrates the zustand store before the app renders. The seed data used to be
 * bundled into the frontend; it now lives in the database, so nothing below
 * this provider may render until the fetch completes — TraceProvider, the
 * shell and every screen read the seeds synchronously via useSeed().
 *
 * Every successful fetch is also durably cached (IndexedDB, see
 * offlineDb.ts) so a reload with zero connectivity can still start the app
 * from the last known-good copy instead of hard-blocking on the hard error
 * screen below — previously ANY fetch failure here blocked the entire app,
 * not just the two offline-capable forms, even for a returning user whose
 * device simply has no signal right now.
 */
export function SeedProvider({ children }: { children: React.ReactNode }) {
  const hydrated = useStore((s) => s.seedsHydrated);
  const hydrateSeeds = useStore((s) => s.hydrateSeeds);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [cachedAt, setCachedAt] = useState<number | null>(null);

  useEffect(() => {
    if (hydrated) return;
    let cancelled = false;
    setFailed(false);
    api
      .getWorkspaceSeeds()
      .then((seeds) => {
        if (cancelled) return;
        hydrateSeeds(seeds);
        setCachedAt(null);
        void cacheReference(SEED_CACHE_KEY, seeds);
      })
      .catch(async () => {
        if (cancelled) return;
        const cached = await getCachedReference(SEED_CACHE_KEY);
        if (cancelled) return;
        if (cached) {
          hydrateSeeds(cached.data as Record<string, WorkspaceSeed>);
          setCachedAt(cached.cachedAt);
        } else {
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [hydrated, attempt, hydrateSeeds]);

  if (failed) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-white px-6 text-center">
        <p className="text-sm font-medium text-slate-900">Couldn&apos;t load workspace data</p>
        <p className="max-w-sm text-sm text-slate-500">
          The Impact Ledger API isn&apos;t reachable. Check that the backend is running, then try
          again.
        </p>
        <button
          type="button"
          onClick={() => setAttempt((n) => n + 1)}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!hydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <p className="text-sm text-slate-400">Loading workspace…</p>
      </div>
    );
  }

  return (
    <>
      {cachedAt != null && (
        <div className="flex items-center justify-center gap-2 bg-amber-100 px-3 py-1.5 text-center text-xs font-medium text-amber-900">
          Offline — showing workspace data from {formatRelativeShort(new Date(cachedAt).toISOString())}
        </div>
      )}
      {children}
    </>
  );
}
